import { webApi } from '@mi-front/api-clients';
import { Group } from '@visx/group';
import { QUALITY_TIMELINE } from '../../consts';
import { useViewModel } from '../../store/useViewModel';
import { TimelineChartGridAndAxis } from './TimelineChartGridAndAxis';
import { TimelineChartScrollport } from './TimelineChartScrollport';
import { TimelineChartStationAxis } from './TimelineChartStationAxis';
import { TimelineStationRow } from './TimelineStationRow';
import { useTimelineReportIndex } from './useTimelineReportIndex';
import { useTimelineChartGeometry } from './useTimelineChartGeometry';
import { useTimelineChartInteractions } from './useTimelineChartInteractions';

export type TimelineChartProps = {
  width: number;
  report: webApi.StreamQualityReportResponseDto;
  /**
   * When false, omits the top time axis and top margin so the axis can be rendered separately
   * (e.g. in a sticky header with `TimelineAxisStrip`).
   */
  includeTopAxis?: boolean;
  /**
   * When set, renders only these station rows (e.g. per UTC-offset group). When omitted, uses
   * stations allowed by the status chip filter.
   */
  stationIds?: string[];
};

/**
 * Station rows × time: online baseline, hatched strips for `audioQualityScope.disabledIntervals`,
 * then poor-quality vs silent issue hues and offline stream gaps on top.
 * Disabled-interval hatch uses `QUALITY_TIMELINE_DISABLED_HATCH.fill`; `DisabledHatchPatternDef` in `ReportResult` must be mounted.
 * X domain follows `visibleRange` (e.g. driven by the overview brush above this chart).
 * Set `includeTopAxis={false}` when the axis is rendered in a sticky strip above this chart.
 * Hover tooltips are driven via `useReportViewStore` and rendered in `TimelineTooltipLayer`.
 */
export function TimelineChart({
  width,
  report,
  includeTopAxis = true,
  stationIds: stationIdsProp,
}: TimelineChartProps) {
  const { visibleRange, filteredStationIds } = useViewModel(report);
  const stationIds = stationIdsProp ?? filteredStationIds;

  const { globalStart, globalEnd, stationRowsById } = useTimelineReportIndex(report);
  const { isValid, geometry } = useTimelineChartGeometry({
    width,
    includeTopAxis,
    stationIds,
    visibleRange,
    globalStart,
    globalEnd,
  });

  const {
    hoveredStationId,
    setHoveredStationId,
    finder,
    showHover,
    hideTimelineTooltip,
    onStationRowMouseLeave,
    openIssueInLogger,
  } = useTimelineChartInteractions();

  if (!isValid || !geometry) {
    return null;
  }

  const { topMargin, height, innerWidth, innerHeight, xScale, yScale, visibleStart, visibleEnd } =
    geometry;

  return (
    <TimelineChartScrollport>
      <svg width={width} height={height}>
        <Group left={QUALITY_TIMELINE.margin.left} top={topMargin}>
          <TimelineChartGridAndAxis
            xScale={xScale}
            yScale={yScale}
            innerWidth={innerWidth}
            innerHeight={innerHeight}
            stationIds={stationIds}
            includeTopAxis={includeTopAxis}
          />
          <TimelineChartStationAxis
            yScale={yScale}
            stationIds={stationIds}
            hoveredStationId={hoveredStationId}
            finder={finder}
          />
          {stationIds.map((stationId) => {
            const y = yScale(stationId);
            const rowBandHeight = yScale.bandwidth();
            if (y === undefined) {
              return null;
            }
            return (
              <TimelineStationRow
                key={stationId}
                stationId={stationId}
                stationRow={stationRowsById.get(stationId)}
                y={y}
                rowBandHeight={rowBandHeight}
                innerWidth={innerWidth}
                xScale={xScale}
                visibleStart={visibleStart}
                visibleEnd={visibleEnd}
                globalEnd={globalEnd}
                onRowMouseEnter={() => setHoveredStationId(stationId)}
                onRowMouseLeave={onStationRowMouseLeave}
                showHover={showHover}
                hideTimelineTooltip={hideTimelineTooltip}
                openIssueInLogger={openIssueInLogger}
              />
            );
          })}
        </Group>
      </svg>
    </TimelineChartScrollport>
  );
}
